import { useState, useEffect, useRef } from "react";
import { addError, logger } from "@wayflyer/blackbox-fe";
import type { NavigationProps, NavItemButton, NavItemLink, NavSection, NavIconOverlayProps } from "@wayflyer/flyui";
import { NavIconOverlay, useRiveLoaderQuery, useToast, ProductLogo } from "@wayflyer/flyui";
import { useFeatureFlagEnabled } from "posthog-js/react";
import {
  IconCity20Line,
  IconGift20Line,
  IconSettings20Line,
  IconLogOut20Line,
  IconLotOfCash20Line,
  IconReports20Line,
  IconCreditCards20Line,
  IconGraphUp20Line,
  IconWrench20Line,
  IconOpenBook20Line,
  IconBank20Line,
  IconEmptyPage20Line,
  IconOpenNewWindow20Line,
  IconHeadsetHelp20Line,
  IconHome20Line,
} from "@wayflyer/flyui-icons/20/line";
import { BaseSchemas } from "api-types";
import type { ReactNode } from "react";
import { useIntl } from "react-intl";
import type { NavigateOptions } from "react-router-dom";
import { useErrorModal, useInviteModalActions, useLocalStorage } from "shared-ui";

import { useJoinAnalyticsPilotMutation } from "../../../analytics-pilot/queries";
import { MAX_VISIBLE_ACCOUNTS } from "../../../banking/constants";
import { useIntercomLoaded } from "../intercom";
import { useBookkeepingWaitlistQuery } from "../../../bookkeeping/queries";

import { getNavItemNameFromId } from "./nav-item-strings";

declare global {
  interface Window {
    Optanon: {
      ToggleInfoDisplay: () => void;
    };
  }
}

type RiveData = NavIconOverlayProps["riveData"];
type IconRiveConfig = { src: string; gradient?: boolean };

/**
 * Canonical list of nav icon keys used in the sidenav.
 *
 * Adding a key here enforces that getIconComponent handles it (via the switch)
 * and makes it available for ICON_RIVE_MAP. If a new nav item appears from the
 * backend with an icon not in this list, TypeScript will flag the gap.
 */
const NAV_ICON_KEYS = [
  "home",
  "bank",
  "banking",
  "account",
  "payments",
  "cards",
  "transactions",
  "line-chart",
  "documents",
  "share",
  "help",
  "connections",
  "internal",
  "accounting",
  "chief",
] as const;

type NavIconKey = (typeof NAV_ICON_KEYS)[number];

/**
 * Maps every NavIconKey to its Rive animation config.
 *
 * Every key in NavIconKey must have an entry here. If a top-level nav icon
 * returned by the backend is missing from this map, Rive overlays are disabled
 * for ALL items to avoid a mix of animated and static icons, and the gap is
 * reported via logger.warn.
 */
const ICON_RIVE_MAP: Record<NavIconKey, IconRiveConfig> = {
  home: { src: "/fly-assets/animations/icon_home-v4.riv" },
  bank: { src: "/fly-assets/animations/icon_a-lot-of-cash-v4.riv" },
  banking: { src: "/fly-assets/animations/icon_bank-a-v4.riv" },
  account: { src: "/fly-assets/animations/icon_bank-a-v4.riv" },
  payments: { src: "/fly-assets/animations/icon_wallet-v4.riv" },
  cards: { src: "/fly-assets/animations/icon_credit-cards-v4.riv" },
  transactions: { src: "/fly-assets/animations/icon_data-transfer-up-v4.riv" },
  "line-chart": { src: "/fly-assets/animations/icon_report-v4.riv" },
  documents: { src: "/fly-assets/animations/icon_multy-pages-v4.riv" },
  share: { src: "/fly-assets/animations/icon_gift-v4.riv" },
  help: { src: "/fly-assets/animations/icon_open-new-window-v4.riv" },
  connections: { src: "/fly-assets/animations/icon_wrech-v4.riv" },
  internal: { src: "/fly-assets/animations/icon_wrench-v4.riv" },
  accounting: { src: "/fly-assets/animations/icon_open-book-v4.riv" },
  chief: { src: "/fly-assets/animations/icon_chief-v4.riv", gradient: true },
};

export const CUSTOMER_NAV_RIVE_SRCS = [...new Set(Object.values(ICON_RIVE_MAP).map(({ src }) => src))];

function wrapIconWithOverlay(icon: ReactNode, iconKey: NavIconKey, riveData: RiveData | undefined): ReactNode {
  const config = ICON_RIVE_MAP[iconKey];
  if (!riveData?.assets.has(config.src)) {
    return icon;
  }
  return (
    <NavIconOverlay riveSrc={config.src} riveData={riveData} gradient={config.gradient}>
      {icon}
    </NavIconOverlay>
  );
}

function getIconComponent(icon: NavIconKey, riveData?: RiveData): ReactNode {
  let staticIcon: ReactNode;
  switch (icon) {
    case "home":
      staticIcon = <IconHome20Line />;
      break;
    case "bank":
      staticIcon = <IconLotOfCash20Line />;
      break;
    case "line-chart":
      staticIcon = <IconReports20Line />;
      break;
    case "help":
      staticIcon = <IconOpenNewWindow20Line />;
      break;
    case "connections":
      staticIcon = <IconWrench20Line />;
      break;
    case "banking":
      staticIcon = <IconCity20Line />;
      break;
    case "internal":
      staticIcon = <IconWrench20Line />;
      break;
    case "share":
      staticIcon = <IconGift20Line />;
      break;
    case "account":
      staticIcon = <IconBank20Line />;
      break;
    case "payments":
      staticIcon = <IconGraphUp20Line />;
      break;
    case "cards":
      staticIcon = <IconCreditCards20Line />;
      break;
    case "transactions":
      staticIcon = <IconOpenBook20Line />;
      break;
    case "documents":
      staticIcon = <IconEmptyPage20Line />;
      break;
    case "accounting":
      staticIcon = <IconOpenBook20Line />;
      break;
    case "chief":
      staticIcon = <ProductLogo.Chief />;
      break;
  }
  return wrapIconWithOverlay(staticIcon, icon, riveData);
}

const NAV_ICON_KEY_SET: ReadonlySet<string> = new Set<string>(NAV_ICON_KEYS);

function asNavIconKey(icon: string): NavIconKey | undefined {
  if (NAV_ICON_KEY_SET.has(icon)) {
    return icon as NavIconKey;
  }
  logger.warn(`Unknown nav icon key "${icon}" — add it to NAV_ICON_KEYS, getIconComponent, and ICON_RIVE_MAP`);
  return undefined;
}

const navItemMatches = (path: string, alternativePaths: string[] = [], pathname: string): boolean => {
  if (path === pathname || alternativePaths.includes(pathname)) {
    return true;
  } else {
    return alternativePaths.some((alternativePath) => pathname.startsWith(alternativePath));
  }
};

export const useNavigationItems = ({
  appConfig,
  clientNavigateFn,
}: {
  appConfig: BaseSchemas.CustomerAppConfig;
  clientNavigateFn: (path: string, options?: NavigateOptions) => void;
}): NavigationProps => {
  const intl = useIntl();
  const { navItems, customerDomain } = appConfig;
  // Rive overlays are gated behind the "animated-side-nav-icons" feature flag.
  // Disable overlays if any top-level icon lacks a Rive mapping to avoid a mix
  // of animated and static icons.
  const animatedIconsEnabled = useFeatureFlagEnabled("animated-side-nav-icons");
  const accountingNavEnabled = useFeatureFlagEnabled("accounting-nav-enabled");
  const analyticsPilotEnabled = useFeatureFlagEnabled("conjura-analytics-pilot");
  const [analyticsPilotClicked, setAnalyticsPilotClicked] = useLocalStorage<boolean>(
    `analytics-pilot-clicked-${customerDomain}`,
    false,
    true
  );
  const { mutate: joinPilot } = useJoinAnalyticsPilotMutation(customerDomain);
  const allIconsCovered = (navItems ?? []).every((item) => item.icon in ICON_RIVE_MAP);
  const riveResult = useRiveLoaderQuery(CUSTOMER_NAV_RIVE_SRCS);
  const riveData = animatedIconsEnabled && allIconsCovered ? riveResult.data : undefined;
  const { openInviteModal } = useInviteModalActions();
  const alternativePathsMap = {};
  const [messageCount, setMessageCount] = useState(0);
  const messengerOpen = useRef(false);
  const isIntercomLoaded = useIntercomLoaded();
  const { data: entry, isPending, isError } = useBookkeepingWaitlistQuery({ enabled: accountingNavEnabled === true });

  useEffect(() => {
    if (isIntercomLoaded) {
      const handleUnreadCountChange = (unreadCount: number) => {
        setMessageCount(unreadCount);
      };
      const handleHide = () => {
        messengerOpen.current = false;
      };
      const handleShow = () => {
        messengerOpen.current = true;
      };

      window.Intercom("onUnreadCountChange", handleUnreadCountChange);
      window.Intercom("onHide", handleHide);
      window.Intercom("onShow", handleShow);

      return () => {
        // Clean up listeners
        window.Intercom("off", "onUnreadCountChange", handleUnreadCountChange);
        window.Intercom("off", "onHide", handleHide);
        window.Intercom("off", "onShow", handleShow);
      };
    }
  }, [isIntercomLoaded]);

  for (const navItem of navItems ?? []) {
    if (navItem.alternativePaths) {
      alternativePathsMap[navItem.path] = navItem.alternativePaths;
    }

    if (navItem.subItems) {
      for (const subItem of navItem.subItems) {
        if (subItem.alternativePaths) {
          alternativePathsMap[subItem.path] = subItem.alternativePaths;
        }
      }
    }
  }

  const getIsRouteSelected = (pathname: string): boolean => {
    return navItemMatches(pathname, alternativePathsMap[pathname], location.pathname);
  };

  function getClientSubNavItems(
    navItems: BaseSchemas.CustomerAppConfig["navItems"] | null | undefined
  ): Array<NavItemButton | NavItemLink> | undefined {
    if (!navItems || navItems.length === 0) {
      return;
    }

    return navItems.map(({ id, icon, path }) => {
      const iconKey = asNavIconKey(icon);
      return {
        id,
        href: path,
        icon: iconKey ? getIconComponent(iconKey, riveData) : undefined,
        label: getNavItemNameFromId(intl, id),
      };
    });
  }

  const companySection: NavigationProps["companySection"] =
    appConfig.customerName?.length > 0
      ? {
          label: appConfig.customerName,
          id: "company-section",
          icon: null,
          items: [
            ...(appConfig.customerName?.length > 0
              ? [
                  {
                    id: "settings",
                    label: intl.formatMessage({ id: "Menu: Settings", defaultMessage: "Settings" }),
                    href: `/${appConfig?.customerDomain}/settings`,
                  },
                  {
                    id: "invite",
                    label: intl.formatMessage({ id: "Invite someone", defaultMessage: "Invite a team member" }),
                    onClick: () => openInviteModal(),
                  },
                ]
              : []),
          ],
        }
      : undefined;

  const userProfile: NavigationProps["userProfile"] = {
    name: appConfig.userName,
    email: appConfig.userEmail,
  };

  const NAVIGABLE_SECTIONS = ["banking/payments", "banking/accounts"];

  const waitlistSettled = !isPending && !isError;
  const toaster = useToast();
  const { openErrorModal } = useErrorModal();
  const hasJoinedWaitlist = waitlistSettled && !!entry;
  const onboardedToBookkeeping = hasJoinedWaitlist && entry?.state === "onboarded";
  const hideAccountingNavItem = waitlistSettled && entry?.state === "not_eligible";
  const isNavItemLink = (item: NavItemLink | NavSection): item is NavItemLink => {
    return !("items" in item);
  };
  const toClientNavItem = (navItem: BaseSchemas.NavItem): NavItemLink | NavSection => {
    const { id } = navItem;
    let items = getClientSubNavItems(navItem.subItems);
    const iconKey = asNavIconKey(navItem.icon);
    const icon = iconKey ? getIconComponent(iconKey, riveData) : undefined;
    const label = getNavItemNameFromId(intl, navItem.id);
    const navigableSection = navItem.path && NAVIGABLE_SECTIONS.some((section) => navItem.path.includes(section));

    const isAccountsSection = id.includes("accounts") || navItem.path?.includes("/accounts");

    if (isAccountsSection && Array.isArray(items) && items.length > 0) {
      if (items.length > MAX_VISIBLE_ACCOUNTS) {
        const visibleItems = items.slice(0, MAX_VISIBLE_ACCOUNTS);
        const remainingCount = items.length - MAX_VISIBLE_ACCOUNTS;

        const seeMoreItem: NavItemLink = {
          id: "banking-accounts-see-more",
          label: `See more (${remainingCount})`,
          href: "#",
          icon: undefined,
          navigateFn: () => {
            const accountsPath = navItem.path || "";
            clientNavigateFn(`${accountsPath}?expand=true`);
          },
        };

        items = [...visibleItems, seeMoreItem];
      }

      if (items.length === 1) {
        const singleAccountItem = items[0];
        const accountHref = "href" in singleAccountItem ? singleAccountItem.href : navItem.path;
        return {
          id,
          icon,
          label,
          href: accountHref,
        };
      }
    }

    if (items) {
      return {
        id,
        icon,
        label,
        items,
        ...(navigableSection && {
          href: navItem.path,
        }),
      };
    } else {
      return {
        id,
        icon,
        label,
        href: navItem.path,
      };
    }
  };

  const userSection: NavigationProps["userSection"] = {
    label: appConfig.userName,
    id: "user-section",
    icon: null,
    items: [],
    sections: [
      {
        id: "user-actions",
        items: [
          ...(navItems ?? [])
            .map(toClientNavItem)
            .filter(isNavItemLink)
            .filter((item) => item.href?.includes("customer-referrals")), //@todo: do this on the server,
          ...(window?.Optanon?.ToggleInfoDisplay
            ? [
                {
                  id: "privacy-settings",
                  label: intl.formatMessage({
                    id: "Privacy settings",
                    defaultMessage: "Privacy preferences",
                  }),
                  onClick: () => window.Optanon.ToggleInfoDisplay(),
                  icon: <IconSettings20Line />,
                },
              ]
            : []),
        ],
      },
      {
        id: "auth-actions",
        items: [
          {
            id: "sign-out",
            label: intl.formatMessage({ id: "Sign out", defaultMessage: "Sign out" }),
            critical: true,
            href: "/authn/logout",
            reloadDocument: true,
            icon: <IconLogOut20Line />,
          },
        ],
      },
    ],
  };

  const sections: NavigationProps["sections"] = {
    navItems: (navItems ?? [])
      .map(toClientNavItem)
      .map((item) => {
        if (item.id === "accounting page title" && onboardedToBookkeeping) {
          return { ...item, external: true };
        }
        return item;
      })
      .filter((item) => !item.href?.includes("customer-referrals"))
      .filter((item) => !(hideAccountingNavItem && item.id === "accounting page title")),
    secondaryItems: [
      {
        id: "help",
        label: intl.formatMessage({ id: "help centre title", defaultMessage: "Help center" }),
        href: "https://www.wayflyer.com/help-center",
        external: true,
        icon: null,
      },
    ], //@todo: do this on the server,
  };

  const infoItems = [
    ...(isIntercomLoaded
      ? [
          {
            id: "support",
            label: intl.formatMessage({ id: "support title", defaultMessage: "Support" }),
            onClick: () => {
              if ("Intercom" in window) {
                if (messengerOpen.current) {
                  window.Intercom("hide");
                } else {
                  window.Intercom("show");
                }
              }
            },
            icon: <IconHeadsetHelp20Line />,
          },
        ]
      : []),
  ] as Array<NavItemButton | NavItemLink>;

  const notificationBubbles: Array<{ navItemId: string; label: string; title?: string }> = [];
  if (messageCount > 0) {
    notificationBubbles.push({
      navItemId: "support",
      label: String(messageCount),
      title: `${messageCount} messages require your attention`,
    });
  }
  if (waitlistSettled && !hasJoinedWaitlist) {
    notificationBubbles.push({
      navItemId: "accounting page title",
      label: "New",
      title: "Join the waitlist!",
    });
  }

  return {
    userProfile,
    userSection,
    companySection,
    sections,
    infoItems,
    getIsRouteSelected,
    notificationBubbles: notificationBubbles.length > 0 ? notificationBubbles : undefined,
    alwaysVisibleItemIds: ["invite"],
    enableCollapse: false,
    globalCtas:
      analyticsPilotEnabled && !analyticsPilotClicked
        ? [
            {
              message: intl.formatMessage({
                id: "Analytics pilot banner: message",
                defaultMessage: "Join to get early access to Wayflyer AI",
              }),
              action: {
                content: intl.formatMessage({
                  id: "Analytics pilot banner: cta",
                  defaultMessage: "Join Now",
                }),
                analyticsId: "analytics-pilot-join-banner",
                onAction: () => {
                  joinPilot(undefined, {
                    onSuccess: () => {
                      setAnalyticsPilotClicked(true);
                      toaster.positive(
                        intl.formatMessage({
                          id: "bookkeeping.waitlist.statusSql",
                          defaultMessage: "Thanks — we’ve received your details and will be in touch.",
                        })
                      );
                    },
                    onError: (error: unknown) => {
                      addError("Error joining analytics pilot");
                      openErrorModal({
                        variant: "submit-error",
                        error: new Error("Failed to join analytics pilot", { cause: error }),
                      });
                    },
                  });
                },
              },
              color: "accent" as const,
              matchRoutes: ["/:customerDomain/performance/*"],
            },
          ]
        : undefined,
  };
};
